import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { TestResultsChips } from '@elementary/common-ui/components';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { Totals } from '@elementary/common-ui/types';
import { groupBy } from '@elementary/common-ui/utils/common';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { TestStatusGroup } from 'shared/types';

const TooltipText = styled.div`
  white-space: pre-wrap;
`;

type Props = {
  assetId: string;
  statuses: TestStatusGroup;
  qualityDimension: string;
};

const StatusChipsRenderer = ({
  assetId,
  statuses,
  qualityDimension,
}: Props) => {
  const navigate = useNavigateWithEnv();
  const { formatUrlParam } = useSelectedTreeItem();

  const getTooltipContent = useCallback(
    (status: keyof TestStatusGroup) => {
      const values = statuses[status];
      const grouped = groupBy(values, (val) => val);
      return Object.keys(grouped)
        .map((group) => `${group} (${grouped[group].length})`)
        .join('\n');
    },
    [statuses],
  );

  const tooltips = useMemo(
    () => ({
      errors: <TooltipText>{getTooltipContent('fail')}</TooltipText>,
      warnings: <TooltipText>{getTooltipContent('warn')}</TooltipText>,
      passed: <TooltipText>{getTooltipContent('passed')}</TooltipText>,
      noData: <TooltipText>{getTooltipContent('noData')}</TooltipText>,
      failures: '',
    }),
    [getTooltipContent],
  );

  const totals: Totals = {
    errors: statuses.fail.length,
    warnings: statuses.warn.length,
    passed: statuses.passed.length,
    noData: statuses.noData.length,
    failures: 0,
  };

  return (
    <TestResultsChips
      id={assetId}
      totals={totals}
      navigate={navigate}
      formatUrlParam={formatUrlParam}
      qualityDimension={
        qualityDimension !== 'other' ? qualityDimension : undefined
      }
      tooltips={tooltips}
    />
  );
};

export default StatusChipsRenderer;
